import React, { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Button, Divider, Form, Input, Modal, Select, Tooltip } from "antd";
import type { ModalProps } from "antd";
import moment from "moment";
import type { Moment } from "moment";
import type { FormInstance } from "antd/es/form/Form";
import type { TableColumn } from "../../../lib/interfaces";
import { useAppDispatch, useAppSelector } from "../../..";
import versionsTableUtils from "../../../lib/utils/versionsTableUtils";
import ToggleableVersionsTable from "../../ToggleableVersionsTable/ToggleableVersionsTable";
import {
  checkIsLoading,
  getSelectOptions,
  onlyNumbers,
  toDisplayFormat,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
  useActiveModal,
} from "../../../lib/utils";
import ChangeVersionsTable from "../../common/ChangeVersionsTable";
import { hasAccess } from "../../RolesModule/hasAccess";
import {
  MENU_ACCOUNT_SERVICES,
  MENU_ACCOUNT_SERVICES_WRITE,
  PROLONGATION_WRITE,
} from "../../../lib/const/permissions";
import HistoryAndPropertyModal from "../../HistoryAndPropertyModal/HistoryAndPropertyModal";
import { ReactComponent as DotsIcon } from "../../../img/3dots.svg";
import { ReactComponent as ProlongationIcon } from "../../../img/prolongation-icon.svg";
import { ReactComponent as LoupeIcon } from "../../../img/loupe-icon.svg";
import { ReactComponent as WarningIcon } from "../../../img/warning-round-icon.svg";
import { PRODUCT_STATUSES } from "../../../store/slices/products/const/productStatuses";
import { OfferList } from "../../OfferList/OfferList";
import { useOfferList } from "../../OfferList/utils/useOfferList";
import { FormInfo } from "../../common/FormInfo/FormInfo";
import { productsActions } from "../../../store/slices/products/products";
import { IProduct, Offer } from "../../../lib/types";
import "./ProductParamsTable.less";

interface ComponentProps {
  productParamsForm: FormInstance<any>;
  loadProduct: (newOnDate?: string) => void;
  minCloseDate: Moment | null;
  loadProductVersions: () => Promise<any>;
  handleCloseProduct: (fromDate: string) => void;
  handleCancelProduct: (fromDate: string) => Promise<any>;
  handleActivateProduct: (params: {
    serviceStatusId: number;
    fromDate: string;
    force?: boolean;
  }) => Promise<any>;
  handleСhangeProductStatus: (params: {
    serviceStatusId: number;
    fromDate?: string;
    force: boolean;
    changeChildStatuses?: boolean;
  }) => Promise<any>;
  handleChangeProlongOffering: (params: {
    prolongTypeId: number;
    offeringId: number;
    activationCheck?: boolean;
  }) => void;
  handleСhangeProductQuantity: (params: {
    quantity: number;
    fromDate?: string;
    force: boolean;
  }) => Promise<any>;
  productOnDate: Moment; // Дата запроса в адресной строке
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const ProductParamsTable = (props: ComponentProps) => {
  const {
    minCloseDate,
    loadProduct,
    productParamsForm,
    loadProductVersions,
    handleCloseProduct,
    handleCancelProduct,
    handleActivateProduct,
    handleСhangeProductStatus,
    handleChangeProlongOffering,
    handleСhangeProductQuantity,
    productOnDate,
  } = props;

  const dispatch = useAppDispatch();

  const {
    productLoading,
    product,
    prolongOffering,
    prolongOfferingVersions,
    productVersions,
    productVersionsLoading,
    changeProductLoading,
    changeProductQuantityLoading,
  } = useAppSelector((state) => state.products);

  const { activeModal, activeModalOptions, setActiveModal } = useActiveModal<
    "" | "historyModal" | "selectOfferForProlongation"
  >("");

  const prolongOfferListProps = useOfferList({
    initialOfferListViewMode: "selectOffer",
    initialSearchValues: {
      isStandalone: true,
      onDate: moment(),
      offeringStatusId: 2,
      page: 0,
      size: 20,
    },
  });

  const selectOfferModalProps: ModalProps = useMemo(() => {
    const onCancel = () => {
      setActiveModal("");
      prolongOfferListProps.setSelectedOffer(undefined);
    };

    const onOk = () => {
      if (
        product &&
        prolongOfferListProps.selectedOffer &&
        activeModalOptions?.fromDate &&
        activeModalOptions?.key
      ) {
        onChangeChildTableData(activeModalOptions.key, {
          prolongOffering: prolongOfferListProps.selectedOffer,
          prolongTypeId: 2,
        });
        productParamsForm.setFieldsValue({
          prolongOfferingName: prolongOfferListProps.selectedOffer?.name,
          prolongOfferingId: prolongOfferListProps.selectedOffer?.id,
        });
        // },
        // });
        onCancel();
      }
    };

    return {
      visible: activeModal === "selectOfferForProlongation",
      width: "100%",
      okButtonProps: {
        onClick: onOk,
        disabled: !prolongOfferListProps.selectedOffer,
      },
      okText: "Выбрать предложение",
      cancelButtonProps: {
        style: {
          width: "108px",
        },
        onClick: onCancel,
      },
      onCancel,
    };
  }, [activeModal, prolongOfferListProps.selectedOffer]);

  const handleSetInitialValue = (field) => {
    setTimeout(() => {
      productParamsForm.setFieldsValue(field);
    }, 0);
  };

  const [childTableData, setChildTableData] = useState<any>([]);

  const onChangeChildTableData = (key: string, values: { [key: string]: any }) => {
    setChildTableData((prev) =>
      prev.map((prevItem) => {
        if (prevItem.key === key) {
          return {
            ...prevItem,
            ...values,
          };
        }
        return prevItem;
      })
    );
  };

  const onChancelClosing = () => {
    setChildTableData((prevState) =>
      prevState.map((item) => ({
        ...item,
        isClosing: false,
      }))
    );
    productParamsForm.resetFields();
  };

  const isChangingForbidden = useMemo(() => versionsTableUtils.getIsTableChanging(childTableData), [
    childTableData,
  ]);

  const showHistoryModal = ({ id }) => {
    setActiveModal("historyModal", { id });
  };

  const closeActiveModal = () => {
    setActiveModal("");
  };

  const serviceStatus = useAppSelector((state) => state.rtkDictionaries.serviceStatus);

  // const filteredServiceStatuses = useMemo(() => {
  //   switch (product?.serviceType.serviceModeId) {
  //     case 1:
  //       return serviceStatus.filter(({ id }) =>
  //         [
  //           PRODUCT_STATUSES.PROVIDED,
  //           PRODUCT_STATUSES.CANCELLED,
  //           PRODUCT_STATUSES.PAYMENT_EXPECTED,
  //         ].includes(id as any)
  //       );
  //     case 2:
  //       return serviceStatus.filter(({ id }) =>
  //         [
  //           PRODUCT_STATUSES.PENDING,
  //           PRODUCT_STATUSES.ACTIVE,
  //           PRODUCT_STATUSES.BLOCKED,
  //           PRODUCT_STATUSES.CANCELLED,
  //         ].includes(id as any)
  //       );
  //     case 3:
  //       return serviceStatus.filter(({ id }) =>
  //         [
  //           PRODUCT_STATUSES.PENDING,
  //           PRODUCT_STATUSES.ACTIVE,
  //           PRODUCT_STATUSES.BLOCKED,
  //           PRODUCT_STATUSES.CANCELLED,
  //         ].includes(id as any)
  //       );
  //     default:
  //       return serviceStatus;
  //   }
  // }, [product, serviceStatus]);

  const serviceMode = useAppSelector((state) => state.rtkDictionaries.serviceMode);

  const offeringProlongTypes = useAppSelector((state) => state.dicts.offeringProlongTypes);

  const offeringProlongTypesLoading = useAppSelector(
    (state) => state.dicts.offeringProlongTypesLoading
  );

  const serviceTypes = useAppSelector((state) => state.service.serviceTypes) as any[];

  const getProlongOfferTitle = (prolongOffering: Partial<Offer> | undefined): ReactNode | "" => {
    if (prolongOffering) {
      const { extName, name, id } = prolongOffering;
      return [extName, name, id].map((item, idx) => <span key={idx}>{item}</span>);
    }
    return "";
  };

  const columnsWidths = {
    quantity: "10%",
    serviceStatusId: "10%",
    serviceTypeId: "13%",
    // serviceModeId: "13%",
    prolongTypeId: "13%",
    prolongOffering: "30%",
    period: "30%",
    actions: "144px",
    // eye: "48px",
    buttons: "42px",
  };

  const getColumns = (): TableColumn[] => [
    {
      title: "Статус",
      dataIndex: "serviceStatusId",
      key: "serviceStatusId",
      width: columnsWidths.serviceStatusId,
      render: (serviceStatusId) => serviceStatus.find(({ id }) => id === serviceStatusId)?.name,
    },
    {
      title: "Количество",
      dataIndex: "quantity",
      key: "quantity",
      width: columnsWidths.quantity,
    },
    {
      title: "Тип продукта",
      dataIndex: "serviceTypeId",
      key: "serviceTypeId",
      width: columnsWidths.serviceTypeId,
      render: (serviceTypeId) => serviceTypes.find(({ id }) => id === serviceTypeId)?.name,
    },
    // {
    //   title: "Вид",
    //   dataIndex: "serviceModeId",
    //   key: "serviceModeId",
    //   width: columnsWidths.serviceModeId,
    //   render: (serviceModeId) => serviceMode.find(({ id }) => id === serviceModeId)?.name,
    // },
    {
      title: "Тип пролонгации",
      dataIndex: "prolongTypeId",
      key: "prolongTypeId",
      width: columnsWidths.prolongTypeId,
      render: (prolongTypeId, { offering, prolongOfferingId }) => {
        const prolongTypeName = offeringProlongTypes.find(({ id }) => {
          return id === prolongTypeId;
        })?.name;
        const shouldNotify = (() => {
          if (!prolongOfferingId || prolongOfferingId === offering.id) {
            if (prolongTypeId === 2 && offering.prolongTypeId === 1) {
              return true;
            }
          }
          return false;
        })();
        return (
          <div style={{ display: "flex" }}>
            <div>{prolongTypeName}</div>
            {shouldNotify && (
              <Tooltip title="У Предложения выключена пролонгация">
                <WarningIcon style={{ margin: "-4px 0 0 2px" }} />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Предложение для пролонгации",
      key: "prolongOffering",
      dataIndex: "prolongOffering",
      width: columnsWidths.prolongOffering,
      render: (prolongOffering: Offer) => (
        <Tooltip
          title={getProlongOfferTitle(prolongOffering)}
          overlayClassName="product-params__prolongOffer"
        >
          {prolongOffering?.name}
        </Tooltip>
      ),
    },
    {
      title: "Период",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
    },
    {
      key: "actions",
      width: columnsWidths.actions,
    },
    {
      key: "buttons",
      width: columnsWidths.buttons,
      render: (text, record) => {
        return (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              showHistoryModal({ id: record.id });
            }}
            className="iconButton"
            type="text"
            htmlType="button"
            icon={<DotsIcon />}
          />
        );
      },
    },
  ];

  const [columns, setColumns] = useState<TableColumn[]>(getColumns());

  // Обновление колонок по обновлении данных с сохранением hidden
  useEffect(() => {
    const newColumns = getColumns();
    setColumns((prevColumns) =>
      prevColumns.map((column) => {
        if (column.render) {
          const newColumn = newColumns.find(({ key }) => key === column.key);
          return {
            ...column,
            render: newColumn?.render,
          };
        }
        return column;
      })
    );
  }, [
    product,
    serviceStatus,
    serviceTypes,
    serviceMode,
    offeringProlongTypes,
    // changeProlongationMode,
    isChangingForbidden,
  ]);

  const tableData = useMemo(
    () => [
      {
        key: product?.id,
        id: product?.id,
        quantity: product?.quantity || 1,
        serviceStatusId: product?.serviceStatusId,
        serviceTypeId: product?.serviceTypeId,
        serviceModeId: product?.serviceType.serviceModeId,
        prolongTypeId: product?.prolongTypeId,
        prolongOfferingId: product?.prolongOfferingId,
        prolongOffering,
        offering: product?.offering,
        period: product
          ? `${toDisplayFormat(product?.fromDate)} - ${toDisplayFormat(product?.toDate)}`
          : "",
      },
    ],
    [product, serviceStatus, serviceTypes, serviceMode, offeringProlongTypes, prolongOffering]
  );

  const getChildColumns = () => [
    {
      title: "Статус",
      dataIndex: "serviceStatusId",
      key: "serviceStatusId",
      width: columnsWidths.serviceStatusId,
      render: (serviceStatusId, record) => {
        // console.log({ record });
        if (record.isCreated || record.isEditing) {
          handleSetInitialValue({
            serviceStatusId,
          });
          const onSelect = (serviceStatusId) => {
            if (serviceStatusId === PRODUCT_STATUSES.PROVIDED) {
              Modal.info({
                title: "Продукт будет закрыт датой оказания",
                okText: "Ок",
                okButtonProps: {
                  style: {
                    width: "108px",
                  },
                },
              });
            }
          };
          return (
            <Form.Item
              name="serviceStatusId"
              className="form-item-required__hidden-error__mb0"
              rules={[{ required: true }]}
            >
              <Select
                options={getSelectOptions({
                  list: record.isCreated
                    ? serviceStatus
                    : serviceStatus.filter(
                        ({ id }) =>
                          ![PRODUCT_STATUSES.CANCELLED, PRODUCT_STATUSES.PROVIDED].includes(
                            id as any
                          )
                      ),
                })}
                onSelect={onSelect}
              />
            </Form.Item>
          );
        }
        return serviceStatus.find(({ id }) => id === serviceStatusId)?.name;
      },
    },
    {
      title: "Количество",
      dataIndex: "quantity",
      key: "quantity",
      width: columnsWidths.quantity,
      render: (quantity, record) => {
        if (record.isCreated || record.isEditing) {
          handleSetInitialValue({
            quantity,
          });
          return (
            <Form.Item
              name="quantity"
              className="form-item-required__hidden-error__mb0"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input
                onInput={(e) => {
                  onlyNumbers(e, { withoutZero: true });
                }}
              />
            </Form.Item>
          );
        }
        return quantity;
      },
    },
    {
      title: "Тип продукта",
      dataIndex: "serviceTypeId",
      key: "serviceTypeId",
      width: columnsWidths.serviceTypeId,
      render: (serviceTypeId) => serviceTypes.find(({ id }) => id === serviceTypeId)?.name,
    },
    // {
    //   title: "Вид",
    //   dataIndex: "serviceModeId",
    //   key: "serviceModeId",
    //   width: columnsWidths.serviceModeId,
    //   render: (serviceModeId) => serviceMode.find(({ id }) => id === serviceModeId)?.name,
    // },
    {
      title: "Тип пролонгации",
      dataIndex: "prolongTypeId",
      key: "prolongTypeId",
      width: columnsWidths.prolongTypeId,
      render: (prolongTypeId, { key, isLatestVersion, isChangingProlongation }) => {
        if (isChangingProlongation && isLatestVersion) {
          const onChange = (id) => {
            onChangeChildTableData(key, { prolongTypeId: id });
            if (id === 1) {
              prolongOfferListProps.setSelectedOffer(undefined);
              // Если предложение для пролонгации было выбрано ранее, то по выбору "без пролонгации", его сброс
              onChangeChildTableData(key, { prolongOffering: undefined });
              productParamsForm.setFieldsValue({
                prolongOfferingName: undefined,
                prolongOfferingId: undefined,
              });
            }
          };
          return (
            <Form.Item
              name="prolongTypeId"
              className="form-item-required__hidden-error__mb0"
              // initialValue={prolongTypeId}
            >
              <Select
                options={getSelectOptions({
                  list: offeringProlongTypes,
                })}
                loading={checkIsLoading(offeringProlongTypesLoading)}
                style={{ width: "110px" }}
                onChange={onChange}
              />
            </Form.Item>
          );
        }
        return offeringProlongTypes.find(({ id }) => id === prolongTypeId)?.name;
      },
    },
    {
      title: "Предложение для пролонгации",
      dataIndex: "prolongOffering",
      key: "prolongOffering",
      width: columnsWidths.prolongOffering,
      render: (
        v,
        {
          key,
          prolongTypeId,
          prolongOffering,
          offering,
          isLatestVersion,
          fromDate,
          isChangingProlongation,
        }
      ) => {
        const isEditable = prolongTypeId === 2 || (!prolongTypeId && offering?.prolongTypeId === 2);
        const openSearchModal = () => {
          setActiveModal("selectOfferForProlongation", { key, fromDate });
          prolongOfferListProps.setSelectedOffer(undefined);
        };
        if (isChangingProlongation && isLatestVersion) {
          return (
            <div style={{ display: "flex" }}>
              <div style={{ paddingTop: "2px" }}>
                <Tooltip
                  title={getProlongOfferTitle(prolongOffering)}
                  overlayClassName="product-params__prolongOffer"
                >
                  <Form.Item
                    name="prolongOfferingName"
                    className="form-item-required__hidden-error__mb0"
                    // rules={[{ required: true }]}
                  >
                    <Input
                      onClick={openSearchModal}
                      disabled={!isEditable}
                      // Изменение значения вводом не предпологается, поэтому на попытку изменения записывается старое значение
                      onChange={() => {
                        productParamsForm.setFieldsValue({
                          prolongOfferingName: prolongOffering?.name,
                        });
                      }}
                      placeholder={prolongOffering || !isEditable ? "" : "Выберите предложение"}
                      prefix={
                        isEditable && (
                          <div>
                            <LoupeIcon
                              onClick={openSearchModal}
                              style={{ cursor: "pointer", margin: "1px 6px 0 0" }}
                            />
                          </div>
                        )
                      }
                    />
                  </Form.Item>
                </Tooltip>
              </div>
            </div>
          );
        }
        return (
          <Tooltip
            title={getProlongOfferTitle(prolongOffering)}
            overlayClassName="product-params__prolongOffer"
          >
            {prolongOffering?.name}
          </Tooltip>
        );
      },
    },
    {
      title: "Период",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      render: (v, record) => {
        const disabledToDate = (date: moment.Moment) => {
          const a: any = 1;
          console.log({ a });
          if (record.isClosing) {
            // TODO проверить корректность работы функции
            // if (record?.offering?.tariff?.tariffModelId === 2) {
            //   const availableDate = (() => {
            //     const periodTypeId = product?.offering?.tariff?.periodTypeId;
            //     const lifespanPeriodQty = product?.offering?.lifespanPeriodQty || 1;
            //     switch (periodTypeId) {
            //       case 1:
            //         return moment(record.fromDate).add(1 * lifespanPeriodQty, "month");
            //       case 2:
            //         return moment(record.fromDate).add(1 * lifespanPeriodQty, "quarter");
            //       case 3:
            //         return moment(record.fromDate).add(2 * lifespanPeriodQty, "quarter");
            //       case 4:
            //         return moment(record.fromDate).add(1 * lifespanPeriodQty, "year");
            //       default:
            //         return moment(record.fromDate);
            //     }
            //   })();
            //   return date.isBefore(record.fromDate) || date.isAfter(availableDate);
            // }
            if (minCloseDate) {
              // Если дата до начала действия или раньше минимально допустимой даты (minCloseDate) — отключаем
              return (
                date.isBefore(record.fromDate) || date.isBefore(moment(minCloseDate).startOf("day"))
              );
            }
            return date.isBefore(record.fromDate);
          }
          return date.isBefore(record.fromDate) || date.isAfter(record.toDate);
        };
        return (
          <ChangeVersionsTable.Period
            record={record}
            setFieldsValue={productParamsForm.setFieldsValue}
            fromDateRequired
            toDateRequired
            disabledToDate={disabledToDate}
          />
        );
      },
    },
    {
      key: "actions",
      width: columnsWidths.actions,
      render: (value, record) => {
        const isHasAccess = hasAccess(MENU_ACCOUNT_SERVICES_WRITE);
        const onCancelChangingProlongation = () => {
          onChangeChildTableData(record.key, {
            isChangingProlongation: false,
            prolongOffering: record.initialProlongOffering,
            prolongTypeId: record.initialProlongTypeId,
          });
        };

        const isEditableProlongation = (() => {
          if (
            !hasAccess ||
            record.isPastVersion ||
            !record.isLatestVersion ||
            versionsTableUtils.getIsRecordChanging(record)
          ) {
            return false;
          }

          if (product?.offering.prolongTypeId !== 2) {
            return hasAccess(PROLONGATION_WRITE);
          }
          if (product?.offering.prolongTypeId === 2) {
            return true;
          }
          return false;
        })();

        if (record.serviceStatusId && [103, 5].includes(record.serviceStatusId)) {
          return null;
        }

        return (
          <>
            {isEditableProlongation && (
              <Tooltip title="Изменить условия пролонгации">
                <Button
                  type="text"
                  htmlType="button"
                  className="button-icon"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    onChangeChildTableData(record.key, { isChangingProlongation: true });
                    handleSetInitialValue({
                      prolongOfferingId: record.prolongOffering?.id,
                      prolongOfferingName: record.prolongOffering?.name,
                      prolongTypeId: record.prolongTypeId,
                    });
                  }}
                  disabled={isChangingForbidden}
                  icon={<ProlongationIcon />}
                />
              </Tooltip>
            )}
            <ChangeVersionsTable.AddButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.EditButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.CloseButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.SubmitButton record={record} submit={productParamsForm.submit} />
            <ChangeVersionsTable.CancelButton
              record={record}
              resetFields={() => {
                productParamsForm.resetFields();
                if (record.isChangingProlongation) {
                  onCancelChangingProlongation();
                }
              }}
              setChildTableData={setChildTableData}
            />
          </>
        );
      },
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
    },
  ];

  useEffect(() => {
    setChildTableData(
      productVersions
        .map((productVersion, i) => ({
          id: productVersion?.id,
          quantity: productVersion?.quantity || 1,
          serviceStatusId: productVersion?.serviceStatusId,
          serviceTypeId: productVersion?.serviceTypeId,
          serviceModeId: product?.serviceType.serviceModeId,
          initialProlongTypeId: productVersion.prolongTypeId,
          prolongTypeId: productVersion.prolongTypeId,
          initialProlongOffering: prolongOfferingVersions[productVersion.fromDate],
          prolongOffering: prolongOfferingVersions[productVersion.fromDate],
          period: `${toDisplayFormat(productVersion?.fromDate)} - ${toDisplayFormat(
            productVersion?.toDate
          )}`,
          offering: productVersion.offering,
          fromDate: productVersion?.fromDate,
          toDate: productVersion?.toDate,
          ...versionsTableUtils.getCommonChildTableProps(productVersion, i, productVersions.length),
        }))
        .sort(versionsTableUtils.sortVersionsByDate)
    );
  }, [
    product,
    productVersions,
    serviceStatus,
    serviceTypes,
    serviceMode,
    offeringProlongTypes,
    prolongOfferingVersions,
  ]);

  const [childColumns, setChildColumns] = versionsTableUtils.useChildColumns(getChildColumns, [
    isChangingForbidden,
  ]);

  // Обновление колонок версий по обновлении данных с сохранением hidden
  useEffect(() => {
    const newColumns = getChildColumns();
    setChildColumns((prevColumns) =>
      prevColumns.map((column) => {
        if (column.render) {
          const newColumn = newColumns.find(({ key }) => key === column.key);
          return {
            ...column,
            render: newColumn?.render,
          };
        }
        return column;
      })
    );
  }, [serviceStatus, serviceTypes, serviceMode, offeringProlongTypes, product]);

  // Нет более ранних версий Продуктов с атрибутом serviceStatusId в статусе Активен
  const hasActiveEarlierVersions = (fromDate: string) => {
    const earlierVersions = productVersions.filter((version) =>
      moment(version.fromDate).isBefore(fromDate)
    );
    return earlierVersions.some(
      ({ serviceStatusId }) => serviceStatusId === PRODUCT_STATUSES.ACTIVE
    );
  };

  const onFinishEditing = (props: { serviceStatusId: number; quantity: `${number}` }) => {
    if (!product) {
      return console.error("Отсутствуют данные по продукту");
    }
    const { serviceStatusId, quantity } = props;
    const changableRecord = versionsTableUtils.getChangeableRecord(childTableData);
    const fromDate = changableRecord.fromDate
      ? toHttpFormatStartOfDay(changableRecord.fromDate)
      : "";
    const isStatusChanged = serviceStatusId !== changableRecord.serviceStatusId;
    const isQuantityChanged = String(quantity) !== String(changableRecord.quantity);

    const onСhangeProductQuantity = () => {
      if (isQuantityChanged) {
        return handleСhangeProductQuantity({
          fromDate,
          force: true,
          quantity: Number(quantity),
        });
      }
      return Promise.resolve();
    };

    const changeMode = (() => {
      if (isStatusChanged) {
        if (!hasActiveEarlierVersions(fromDate) && serviceStatusId === PRODUCT_STATUSES.ACTIVE) {
          return 1;
        }
        if (product.bounded) {
          return 2;
        }
        return 3;
      }
      if (isQuantityChanged) {
        return 4;
      }
    })();

    if (!changeMode) {
      // Если статус и количество не изменились - очистка и отмена редактирования
      setChildTableData((prevState) =>
        prevState.map((item) => ({
          ...item,
          isEditing: false,
        }))
      );
      productParamsForm.resetFields();
      Modal.warning({
        title: "Редактирование отменено",
        content: "Изменения отстуствуют",
      });
      return;
    }

    const getSuccessTitle = (isQuantityChangeSuccess: boolean): string | undefined => {
      const titles: Record<typeof changeMode, string | undefined> = isQuantityChangeSuccess
        ? {
            1: "Продукт активирован, количество обновлено",
            2: "Статус продукта и количество обновлены",
            3: "Статус продукта и количество обновлены",
            4: "Количество обновлено",
          }
        : {
            1: "Продукт активирован",
            2: "Статус продукта обновлен",
            3: "Статус продукта обновлен",
            4: undefined,
          };
      return titles[changeMode];
    };

    const onChange = async () => {
      let statusChangeError = false;
      if (changeMode === 1) {
        await handleActivateProduct({ fromDate, serviceStatusId, force: true }).then((result) => {
          if (result.error) {
            statusChangeError = true;
          }
        });
      }
      if (changeMode === 2 || changeMode === 3) {
        await handleСhangeProductStatus({
          fromDate,
          serviceStatusId,
          force: true,
          changeChildStatuses: product.bounded,
        }).then((result) => {
          if (result.error) {
            statusChangeError = true;
          }
        });
      }
      if (statusChangeError) {
        return;
      }
      let quantityChangeErrorMsg = "";
      if (isQuantityChanged) {
        await onСhangeProductQuantity().then((result) => {
          if (result.error) {
            quantityChangeErrorMsg = result.error.message || "Неизвестная ошибка";
          }
        });
      }
      const title = getSuccessTitle(isQuantityChanged && !quantityChangeErrorMsg);
      const content = quantityChangeErrorMsg ? (
        <div className="product-params-table__errorContent">
          <div>Ошибка изменения количества</div>
          <div>{quantityChangeErrorMsg}</div>
        </div>
      ) : null;
      if (title) {
        Modal.success({ title, content });
      } else if (quantityChangeErrorMsg) {
        Modal.error({ title: "Ошибка изменения количества", content: quantityChangeErrorMsg });
      }
      loadProductVersions().then((response) => {
        if (response.payload) {
          const versions = [...response.payload] as IProduct[];
          versions.sort((a, b) => (moment(a.fromDate).isBefore(b.fromDate) ? 1 : -1));
          // Проверка наличия версии в которую попадает текущая дата запроса (из адресной строки)
          const isOnDateActual = versions.some(({ fromDate, toDate }) =>
            productOnDate.isBetween(fromDate, toDate, "day", "[]")
          );
          // Если существует версия с текущей датой запроса, запрашиваем на эту дату, иначе на fromDate последней версии
          const loadDate = isOnDateActual
            ? toHttpFormatStartOfDay(productOnDate)
            : versions[0].fromDate;
          loadProduct(loadDate);
        }
      });
    };

    if ([1, 3, 4].includes(changeMode)) {
      onChange();
    } else if (changeMode === 2) {
      Modal.confirm({
        onOk: () => {
          onChange();
        },
        title: "Статус будет изменен в том числе у дочерних продуктов. Продолжить?",
        okText: "ОК",
        cancelText: "Отмена",
      });
    }
  };

  const onFinishAddVersion = (props: {
    serviceStatusId: number;
    fromDate: Moment;
    quantity: `${number}`;
  }) => {
    if (!product) {
      return console.error("Отсутствуют данные по продукту");
    }
    const { serviceStatusId, quantity } = props;
    // Версия продукта, на основании которой создается новая
    const baseProductVersion = productVersions.find((version) =>
      props.fromDate.isBetween(version.fromDate, version.toDate)
    );
    const isStatusChanged = serviceStatusId !== baseProductVersion?.serviceStatusId;
    const isQuantityChanged = String(quantity) !== String(baseProductVersion?.quantity);
    const fromDate = toHttpFormatStartOfDay(props.fromDate);

    const changeMode = (() => {
      if (isStatusChanged) {
        if (serviceStatusId === PRODUCT_STATUSES.PROVIDED) {
          return 1;
        }
        if (serviceStatusId === PRODUCT_STATUSES.CANCELLED) {
          return 2;
        }
        if (serviceStatusId === PRODUCT_STATUSES.ACTIVE && !hasActiveEarlierVersions(fromDate)) {
          return 3;
        }
        return 4;
      }
      if (isQuantityChanged) {
        return 5;
      }
    })();

    if (!changeMode) {
      // Если статус и количество не изменились - отмена добавления версии
      setChildTableData((prevState) => prevState.slice(1));
      productParamsForm.resetFields();
      Modal.warning({
        title: "Добавление версии отменено",
        content: "Изменения отстуствуют",
      });
      return;
    }

    const confirmModalTitle = {
      1: "При изменении даты закрытия дочерние Продукты будут закрыты. Вы хотите сохранить изменения?",
      2: "Дочерние Продукты будут также отменены. Вы хотите сохранить изменения?",
      4: "Статус будет изменен в том числе у дочерних продуктов. Продолжить?",
    };

    const getSuccessTitle = (isQuantityChangeSuccess: boolean): string | undefined => {
      const titles: Record<typeof changeMode, string | undefined> = isQuantityChangeSuccess
        ? {
            1: "Статус продукта и количество обновлены",
            2: "Продукт отменен, количество обновлено",
            3: "Продукт активирован, количество обновлено",
            4: "Статус продукта и количество обновлены",
            5: "Количество обновлено",
          }
        : {
            1: "Статус продукта обновлен",
            2: "Продукт отменен",
            3: "Продукт активирован",
            4: "Статус продукта обновлен",
            5: undefined,
          };
      return titles[changeMode];
    };

    const onChange = async (displayConfirmModal?: boolean) => {
      if ([1, 2, 4].includes(changeMode) && product.bounded && displayConfirmModal) {
        Modal.confirm({
          onOk: () => {
            onChange();
          },
          title: confirmModalTitle[changeMode],
          okText: "Сохранить",
          cancelText: "Отмена",
        });
        return;
      }
      let statusChangeError = false;
      if (changeMode === 1) {
        await handleСhangeProductStatus({
          fromDate,
          serviceStatusId,
          force: false,
        }).then((result) => {
          if (result.error) {
            statusChangeError = true;
          }
        });
      } else if (changeMode === 2) {
        await handleCancelProduct(fromDate).then((result) => {
          if (result.error) {
            statusChangeError = true;
          }
        });
      } else if (changeMode === 3) {
        await handleActivateProduct({ fromDate, serviceStatusId, force: false }).then((result) => {
          if (result.error) {
            statusChangeError = true;
          }
        });
      } else if (changeMode === 4) {
        await handleСhangeProductStatus({
          fromDate,
          serviceStatusId,
          force: false,
          changeChildStatuses: product.bounded,
        }).then((result) => {
          if (result.error) {
            statusChangeError = true;
          }
        });
      }
      if (statusChangeError) {
        return;
      }
      let quantityChangeErrorMsg = "";
      if (isQuantityChanged) {
        await handleСhangeProductQuantity({
          fromDate,
          force: isStatusChanged,
          quantity: Number(quantity),
        }).then((result) => {
          if (result.error) {
            quantityChangeErrorMsg = result.error.message || "Неизвестная ошибка";
          }
        });
      }
      const title = getSuccessTitle(isQuantityChanged && !quantityChangeErrorMsg);
      const content = quantityChangeErrorMsg ? (
        <div className="product-params-table__errorContent">
          <div>Ошибка изменения количества</div>
          <div>{quantityChangeErrorMsg}</div>
        </div>
      ) : null;
      if (title) {
        Modal.success({ title, content });
      } else if (quantityChangeErrorMsg) {
        Modal.error({ title: "Ошибка изменения количества", content: quantityChangeErrorMsg });
      }
      loadProduct(fromDate);
      loadProductVersions();
    };

    onChange(true);
  };

  const onFinish = (values) => {
    const changableRecord = versionsTableUtils.getChangeableRecord(childTableData);
    const changingMode = (() => {
      if (changableRecord.isClosing) return "isClosing";
      if (changableRecord.isCreated) return "isCreated";
      if (changableRecord.isEditing) return "isEditing";
      if (changableRecord.isChangingProlongation) return "isChangingProlongation";
    })();
    switch (changingMode) {
      case "isClosing":
        // eslint-disable-next-line no-case-declarations
        const warning = (() => {
          if (product?.bounded) {
            // if (moment(values.toDate).isBefore(changableRecord.toDate, "day")) {
            if (moment(values.toDate).isBefore(moment(), "day")) {
              return (
                <>
                  <div>
                    При изменении даты закрытия дочерние Продукты будут закрыты. Для пролонгации
                    продукта необходимо создать новый продукт.
                  </div>
                  <div>Вы хотите сохранить изменения?</div>
                </>
              );
            }
            return (
              <>
                <div>При изменении даты закрытия дочерние Продукты будут закрыты.</div>
                <div>Вы хотите сохранить изменения?</div>
              </>
            );
          }
          // if (moment(values.toDate).isBefore(changableRecord.toDate, "day")) {
          if (moment(values.toDate).isBefore(moment(), "day")) {
            return <>Для пролонгации продукта необходимо создать новый продукт.</>;
          }
        })();
        if (warning) {
          Modal.confirm({
            onOk: () => handleCloseProduct(toHttpFormatEndOfDay(values.toDate)),
            title: warning,
            okText: "Сохранить",
            cancelText: "Отмена",
            onCancel: onChancelClosing,
            width: 575,
          });
        } else {
          handleCloseProduct(toHttpFormatEndOfDay(values.toDate));
        }
        break;
      case "isCreated":
        onFinishAddVersion(values);
        break;
      case "isEditing":
        onFinishEditing(values);
        break;
      case "isChangingProlongation":
        const prolongOfferingId = productParamsForm.getFieldValue("prolongOfferingId");
        handleChangeProlongOffering({
          prolongTypeId: values.prolongTypeId,
          offeringId: prolongOfferingId,
        });
        break;
      default:
        break;
    }
  };

  return (
    <div className="product-params-table">
      <h3 className="product-params-table__title">Информация о продукте</h3>
      <Divider style={{ margin: "20px 0 0 0 !important" }} />
      {/* <Button
        onClick={() => {
          console.log(productParamsForm.getFieldsValue());
        }}
      >
        get form values
      </Button> */}
      <Form form={productParamsForm} onFinish={onFinish} style={{ marginTop: "-10px" }}>
        <ToggleableVersionsTable
          // style={{ marginTop: "45px" }}
          tableName="product-params-table"
          columns={columns}
          setColumns={setColumns}
          childTableColumns={childColumns}
          setChildTableColumns={setChildColumns}
          tableData={tableData}
          isTableLoading={checkIsLoading(
            productLoading,
            changeProductLoading,
            changeProductQuantityLoading
          )}
          childTableData={childTableData}
          isChildTableLoading={checkIsLoading(productVersionsLoading)}
          isPageable={false}
          onOpenVersionsCallback={() => {
            loadProductVersions();
          }}
          onCloseVersionsCallback={() => {
            dispatch(productsActions.cleanProductversions());
          }}
        />
      </Form>
      <FormInfo
        getFormValues={productParamsForm.getFieldsValue}
        className="product-params-table__formInfo"
      />
      <Modal {...selectOfferModalProps}>
        <OfferList
          {...prolongOfferListProps}
          filterName="changed-prolong-offer"
          disabledSearchFields={["isStandalone", "offeringStatusId"]}
          selectedAgreement={product?.agreement}
        />
      </Modal>
      <HistoryAndPropertyModal
        objectTypeSysname="service"
        objectId={activeModalOptions?.id}
        handleHideModal={closeActiveModal}
        visibleModal={activeModal === "historyModal"}
        hasAccessToEdit={hasAccess(MENU_ACCOUNT_SERVICES, MENU_ACCOUNT_SERVICES_WRITE)}
      />
    </div>
  );
};
